import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Typography,
  Input,
  Button,
  Table,
  Card,
  Avatar,
  Space,
  Dropdown,
  Pagination,
  Row,
  Col,
  Select,
  Badge,
  Tag,
  message,
  Spin,
  Modal
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  StarOutlined,
  SettingOutlined,
  MenuOutlined,
  MoreOutlined,
  SearchOutlined,
  DownOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  UserAddOutlined,
  CalculatorOutlined,
  GlobalOutlined,
  TeamOutlined,
  RiseOutlined,
  GiftOutlined,
  BankOutlined,
  ExclamationCircleOutlined,
  SortAscendingOutlined,
  MailOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import InviteBeneficiaryModal from './InviteBeneficiaryModal';
import BeneficiaryProfile from './BeneficiaryProfile';
import UserProfile from './UserProfile';
import DashboardSection from './DashboardSection';
import BeneficiaryHighlights from './BeneficiaryHighlights';
import { beneficiaryAPI } from '../services/api';
import '../styles/sidebar-standard.css';
import '../styles/menu-hover-overrides.css';
import './Beneficiaries.css';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Beneficiaries: React.FC = () => {
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('30-days');
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<string | null>(null);
  const [selectedBeneficiaryData, setSelectedBeneficiaryData] = useState<any | null>(null);
  const [profileVisible, setProfileVisible] = useState(false);
  const [beneficiariesData, setBeneficiariesData] = useState<any[]>([]);
  const [allBeneficiariesData, setAllBeneficiariesData] = useState<any[]>([]);
  const [highlights, setHighlights] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCause, setSelectedCause] = useState<string | undefined>(undefined);
  const [selectedDuration, setSelectedDuration] = useState<string | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined);
  const [selectedActiveStatus, setSelectedActiveStatus] = useState<'active' | 'inactive' | undefined>(undefined);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalBeneficiaries, setTotalBeneficiaries] = useState(0);
  const [isDeleteBeneficiaryModalVisible, setIsDeleteBeneficiaryModalVisible] = useState(false);
  const [deletingBeneficiary, setDeletingBeneficiary] = useState<any | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Load beneficiaries from API
  const loadBeneficiaries = async () => {
    setLoading(true);
    setError(null);

    // Fire highlights fetch in parallel — independent of the table data.
    beneficiaryAPI
      .getBeneficiaryHighlights()
      .then((r: any) =>
        setHighlights(r?.success ? r.data : null),
      )
      .catch(() => setHighlights(null));

    try {
      const collected: any[] = [];
      let page = 1;
      const limit = Math.max(pageSize, 200);
      let total = 0;
      let response: any = null;

      do {
        response = await beneficiaryAPI.getBeneficiaries(page, limit, { includeInactive: true });
        if (response?.success && Array.isArray(response.data)) {
          collected.push(...response.data);
        }
        total = response?.pagination?.total || collected.length;
        page += 1;
      } while (collected.length < total);
      
      if (response?.success) {
        // Log the actual API response structure for debugging
        if (collected.length > 0) {
          const sample = collected[0];
        }
        
        // Transform API data to match our table structure
        // Handle both new charity structure and legacy beneficiary structure
        // Filter out soft-deleted records (if backend returns them)
        const filteredData = collected.filter((beneficiary: any) => {
          // Exclude soft-deleted records (backend uses is_active=false for soft deletes)
          return !beneficiary.deleted_at &&
                 !beneficiary.deletedAt &&
                 !beneficiary.is_deleted &&
                 !beneficiary.isDeleted &&
                 beneficiary.deleted !== true &&
                 beneficiary.is_active !== false &&
                 beneficiary.isActive !== false;
        });
        
        const transformedData = filteredData.map((beneficiary: any) => {
          // Log the raw beneficiary data for debugging
          
          // Extract all possible field variations from API
          const name = beneficiary.name || beneficiary.beneficiaryName || 'Unknown';
          const category = beneficiary.category || beneficiary.cause || beneficiary.beneficiaryCause || 'General';
          const type = beneficiary.type || beneficiary.beneficiaryType || 'Medium';
          
          // Location - build from available fields
          let location = beneficiary.location || '';
          if (!location && beneficiary.city && beneficiary.state) {
            location = `${beneficiary.city}, ${beneficiary.state}`;
            if (beneficiary.zip_code) {
              location += ` ${beneficiary.zip_code}`;
            }
          } else if (!location && beneficiary.address) {
            location = `${beneficiary.address.city || ''}, ${beneficiary.address.state || ''}`.replace(/^,\s*|,\s*$/g, '');
          }
          if (!location) location = beneficiary.cityState || '';
          
          // Phone - check all variations, but don't default to 'N/A' if field exists but is empty
          const phone = beneficiary.phone || 
                       beneficiary.phoneNumber || 
                       beneficiary.contactNumber || 
                       beneficiary.contact_number || 
                       '';
          
          // Email - Check all possible field variations
          const email = beneficiary.email || 
                       beneficiary.primaryEmail || 
                       beneficiary.primary_email || 
                       beneficiary.contact_email ||
                       beneficiary.contactEmail ||
                       '';
          
          // Contact Name - check all variations
          const contactName = beneficiary.contact_name || 
                             beneficiary.contactName || 
                             beneficiary.primaryContact || 
                             beneficiary.primary_contact || 
                             '';
          
          const createdAt = beneficiary.createdAt || beneficiary.created_at || beneficiary.dateOfJoin || beneficiary.date_of_join;
          // Resolve isActive - do NOT default to true when API omits the field (some APIs omit is_active when false)
          const isActive = beneficiary.isActive !== undefined ? beneficiary.isActive : 
                          (beneficiary.is_active !== undefined ? beneficiary.is_active : 
                          (beneficiary.active !== undefined ? beneficiary.active : undefined));
          
          // Extract logo URL - prioritize logo over main image for table display
          // Logo should be displayed next to the name in the table
          const imageUrl = beneficiary.logo || 
                          beneficiary.logo_url || 
                          beneficiary.logoUrl ||
                          beneficiary.imageUrl || 
                          beneficiary.main_image || 
                          beneficiary.main_image_url || 
                          '';
          
          // Bank account - format if exists
          let bankAccountDisplay = '';
          if (beneficiary.bank_account) {
            const accountStr = beneficiary.bank_account.toString();
            bankAccountDisplay = accountStr.length > 4 ? `****${accountStr.slice(-4)}` : accountStr;
          } else if (beneficiary.bankAccount) {
            const accountStr = beneficiary.bankAccount.toString();
            bankAccountDisplay = accountStr.length > 4 ? `****${accountStr.slice(-4)}` : accountStr;
          }
          
          // Helper function to ensure we never return empty strings or "N/A"
          const ensureValue = (value: any, fallback: string = 'Not provided'): string => {
            if (value === null || value === undefined || value === '' || value === 'N/A' || value === 'n/a') {
              return fallback;
            }
            return String(value);
          };
          
          // Extract all available fields from the API response
          return {
            key: beneficiary.id?.toString() || beneficiary.key || Math.random().toString(),
            beneficiaryName: ensureValue(name, 'Unknown'),
            contactName: ensureValue(contactName || (email ? email : ''), 'Not provided'),
            email: ensureValue(email, 'Not provided'),
            contactNumber: ensureValue(phone, 'Not provided'),
            bankAccount: ensureValue(bankAccountDisplay, 'Not provided'),
            donation: beneficiary.total_donations ? `$${beneficiary.total_donations.toLocaleString()}` : 
                     (beneficiary.totalDonations ? `$${beneficiary.totalDonations.toLocaleString()}` :
                     (beneficiary.donation || '$0')),
            dateOfJoin: createdAt ? new Date(createdAt).toLocaleDateString() : 'Not provided',
            cityState: ensureValue(location, 'Not provided'),
            beneficiaryCause: ensureValue(category, 'General'),
            beneficiaryType: ensureValue(type, 'Medium'),
            donors: beneficiary.donor_count || beneficiary.donorCount || beneficiary.mutual || beneficiary.donors || 0,
            active: isActive,
            enabled: isActive,
            avatar: name ? name.charAt(0).toUpperCase() : 'B',
            imageUrl: imageUrl, // Store image URL for avatar
            // Store full beneficiary data for profile view - preserve ALL fields from API
            rawData: beneficiary
          };
        });
        
        setBeneficiariesData(transformedData);
        setAllBeneficiariesData(transformedData);
        setTotalBeneficiaries(transformedData.length);
      } else {
        setError('Failed to load charities');
        setBeneficiariesData([]);
        setAllBeneficiariesData([]);
        setTotalBeneficiaries(0);
      }
    } catch (error: any) {
      console.error('Error loading charities:', error);
      
      // Check if it's a 404 error (endpoint not ready)
      if (error.message && error.message.includes('404')) {
        setError('Backend endpoint is being prepared. Use "Invite Charity" button to add charities.');
        setBeneficiariesData([]);
        setAllBeneficiariesData([]);
        setTotalBeneficiaries(0);
      } else {
        setError('Failed to load charities');
        setBeneficiariesData([]);
        setAllBeneficiariesData([]);
        setTotalBeneficiaries(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when page changes
  useEffect(() => {
    loadBeneficiaries();
  }, [pageSize]);

  const handleToggleChange = async (key: string, field: 'active' | 'enabled') => {
    const beneficiaryId = parseInt(key, 10);
    if (isNaN(beneficiaryId)) return;
    const record = allBeneficiariesData.find((b) => b.key === key);
    if (!record) return;
    const nextActive = field === 'active' ? !record.active : record.active;
    const nextEnabled = field === 'enabled' ? !record.enabled : record.enabled;
    try {
      const payload = field === 'active'
        ? { is_active: nextActive, isActive: nextActive }
        : { is_enabled: nextEnabled, isEnabled: nextEnabled };
      const response = await beneficiaryAPI.updateBeneficiary(beneficiaryId, payload);
      if (response?.success !== false) {
        setAllBeneficiariesData(prev =>
          prev.map(item =>
            item.key === key
              ? { ...item, active: nextActive, enabled: nextEnabled }
              : item
          )
        );
        setBeneficiariesData(prev =>
          prev.map(item =>
            item.key === key
              ? { ...item, active: nextActive, enabled: nextEnabled }
              : item
          )
        );
        message.success(`${field === 'active' ? 'Active' : 'Enabled'} status updated`);
        loadBeneficiaries(); // Refresh from server to stay in sync
      } else {
        message.error('Failed to update status');
      }
    } catch (err: any) {
      console.error('Toggle error:', err);
      message.error(err?.message || 'Failed to update status');
    }
  };

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, selectedCause, selectedDuration, selectedType, selectedLocation, selectedActiveStatus]);

  const uniqueCauses = Array.from(new Set(allBeneficiariesData.map((b) => b.beneficiaryCause).filter(Boolean)));
  const uniqueTypes = Array.from(new Set(allBeneficiariesData.map((b) => b.beneficiaryType).filter(Boolean)));
  const uniqueLocations = Array.from(new Set(allBeneficiariesData.map((b) => b.cityState).filter(Boolean)));

  const filteredBeneficiaries = allBeneficiariesData.filter((beneficiary) => {
    const matchesSearch = searchTerm
      ? [beneficiary.beneficiaryName, beneficiary.email, beneficiary.contactName, beneficiary.contactNumber, beneficiary.cityState]
          .filter(Boolean)
          .some((value: string) => value.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;

    const matchesCause = selectedCause ? beneficiary.beneficiaryCause === selectedCause : true;
    const matchesType = selectedType ? beneficiary.beneficiaryType === selectedType : true;
    const matchesLocation = selectedLocation ? beneficiary.cityState === selectedLocation : true;

    const matchesDuration = selectedDuration
      ? (() => {
          const durationValue =
            beneficiary?.rawData?.duration ||
            beneficiary?.rawData?.program_duration ||
            beneficiary?.rawData?.project_duration ||
            beneficiary?.rawData?.beneficiary_duration ||
            '';
          return String(durationValue).toLowerCase() === selectedDuration.toLowerCase();
        })()
      : true;

    // Resolve active status from transformed data or rawData (handles API format variations)
    // Some APIs omit is_active when false - treat missing as inactive when filtering for inactive
    const rawActive = beneficiary.active ?? beneficiary.rawData?.is_active ?? beneficiary.rawData?.isActive;
    const hasExplicitValue =
      beneficiary.active !== undefined ||
      beneficiary.rawData?.is_active !== undefined ||
      beneficiary.rawData?.isActive !== undefined;
    const isActiveResolved = hasExplicitValue
      ? (rawActive === true || rawActive === 'true' || rawActive === 1 || rawActive === '1')
      : selectedActiveStatus === 'inactive'
        ? false
        : true;
    const matchesActiveStatus = selectedActiveStatus
      ? (selectedActiveStatus === 'active' && isActiveResolved) ||
        (selectedActiveStatus === 'inactive' && !isActiveResolved)
      : true;

    return matchesSearch && matchesCause && matchesType && matchesLocation && matchesDuration && matchesActiveStatus;
  });

  // Debug: when Inactive filter shows nothing, log what we have
  if (selectedActiveStatus === 'inactive' && filteredBeneficiaries.length === 0 && allBeneficiariesData.length > 0) {
    const breakdown = allBeneficiariesData.reduce(
      (acc, b) => {
        const raw = b.active ?? b.rawData?.is_active ?? b.rawData?.isActive;
        const key = raw === true || raw === 'true' ? 'active' : raw === false || raw === 'false' || raw === 0 ? 'inactive' : 'undefined';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }

  const sortedBeneficiaries = [...filteredBeneficiaries].sort((a, b) => {
    if (!sortField || !sortOrder) return 0;
    const valueA = (a as any)[sortField];
    const valueB = (b as any)[sortField];
    const normalizedA = valueA ? valueA.toString().toLowerCase() : '';
    const normalizedB = valueB ? valueB.toString().toLowerCase() : '';
    const comparison = normalizedA.localeCompare(normalizedB);
    return sortOrder === 'ascend' ? comparison : -comparison;
  });

  const paginatedBeneficiaries = sortedBeneficiaries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleInviteBeneficiary = () => {
    setInviteModalVisible(true);
  };

  const handleInviteModalCancel = () => {
    setInviteModalVisible(false);
  };

  const handleInviteModalSubmit = async (values: any) => {
    // The InviteBeneficiaryModal handles the API call itself
    // This callback is called after successful creation to refresh the list
    setInviteModalVisible(false);
    // Refresh the beneficiaries list to show the newly created beneficiary with all fields
    await loadBeneficiaries();
  };

  // Opened from the approvals queue: approving a charity leaves it hidden
  // until its profile is complete, and lands here with the id to open so the
  // admin can finish it immediately.
  useEffect(() => {
    const st: any = location.state;
    if (!st?.openBeneficiaryId) return;
    setSelectedBeneficiaryId(String(st.openBeneficiaryId));
    setSelectedBeneficiaryData(null);
    setProfileVisible(true);
    if (st.completeProfileFor) {
      message.info({
        content: `Finish ${st.completeProfileFor}'s profile and save — that is what makes it visible to donors.${
          Array.isArray(st.missingFields) && st.missingFields.length
            ? ` Still needed: ${st.missingFields.join(', ')}.`
            : ''
        }`,
        duration: 10,
      });
    }
    // Clear the state so a refresh or back-navigation doesn't reopen it.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleBeneficiaryClick = (beneficiaryId: string, record: any) => {
    setSelectedBeneficiaryId(beneficiaryId);
    setSelectedBeneficiaryData(record.rawData || record);
    setProfileVisible(true);
  };

  const handleProfileClose = () => {
    setProfileVisible(false);
    setSelectedBeneficiaryId(null);
    setSelectedBeneficiaryData(null);
  };

  const handleBeneficiaryUpdate = async (updatedData: any) => {
    // Refresh the beneficiaries list to show updated data
    await loadBeneficiaries();
    // Close the profile
    setProfileVisible(false);
    setSelectedBeneficiaryId(null);
  };

  const handleDeleteBeneficiary = (record: any) => {
    setDeletingBeneficiary(record);
    setIsDeleteBeneficiaryModalVisible(true);
  };

  const confirmDeleteBeneficiary = async () => {
    if (!deletingBeneficiary) {
      message.error('Cannot delete charity: missing charity data');
      return;
    }

    // Try to get ID from multiple possible locations
    const beneficiaryId = deletingBeneficiary.rawData?.id || 
                         deletingBeneficiary.id || 
                         deletingBeneficiary.key ||
                         (deletingBeneficiary.rawData && deletingBeneficiary.rawData.id);
    
    if (!beneficiaryId) {
      console.error('Charity record structure:', deletingBeneficiary);
      message.error('Cannot delete charity: missing charity ID');
      return;
    }

    try {
      setLoading(true);
      // Convert to number if it's a string
      const idToDelete = typeof beneficiaryId === 'string' ? parseInt(beneficiaryId) : beneficiaryId;
      const response = await beneficiaryAPI.deleteBeneficiary(idToDelete);
      const isSuccess = response?.error !== undefined ? false : (response?.success !== false || response?.data !== undefined);
      
      if (isSuccess) {
        message.success(`Charity ${deletingBeneficiary.beneficiaryName || deletingBeneficiary.name} deleted successfully`);
        setIsDeleteBeneficiaryModalVisible(false);
        setDeletingBeneficiary(null);
        
        // Immediately remove from local state (in case backend does soft delete)
        const deletedKey = deletingBeneficiary.key || deletingBeneficiary.id?.toString();
        setBeneficiariesData(prevData => prevData.filter(item => 
          item.key !== deletedKey && 
          item.id?.toString() !== deletedKey &&
          item.rawData?.id?.toString() !== deletedKey
        ));
        setTotalBeneficiaries(prev => Math.max(0, prev - 1));
        
        // Refresh from API to ensure consistency
        await loadBeneficiaries();
      } else {
        message.error(response?.error || response?.message || 'Failed to delete charity');
      }
    } catch (error: any) {
      console.error('Error deleting charity:', error);
      message.error(error.message || 'Failed to delete charity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  const timeFilterMenu = [
    {
      key: '7-days',
      label: 'Last 7 Days',
      icon: <CheckCircleFilled />
    },
    {
      key: '30-days',
      label: 'Last 30 Days',
      icon: <CheckCircleFilled />
    },
    {
      key: '90-days',
      label: 'Last 90 Days',
      icon: <CheckCircleFilled />
    },
    {
      key: '1-year',
      label: 'Last 1 Year',
      icon: <CheckCircleFilled />
    }
  ];

  // No hardcoded data - use API data only

  const columns = [
    {
      title: (
        <div className="sortable-header">
          Charity name
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'beneficiaryName',
      key: 'beneficiaryName',
      sorter: true,
      sortOrder: sortField === 'beneficiaryName' ? sortOrder : null,
      render: (text: string, record: any) => (
        <Space>
          <Avatar 
            size={32} 
            src={record.rawData?.logo || 
                 record.rawData?.logo_url || 
                 record.rawData?.logoUrl ||
                 record.imageUrl || 
                 record.rawData?.imageUrl || 
                 record.rawData?.main_image || 
                 record.rawData?.main_image_url}
            style={{ 
              backgroundColor: (record.rawData?.logo || 
                               record.rawData?.logo_url || 
                               record.rawData?.logoUrl ||
                               record.imageUrl || 
                               record.rawData?.imageUrl || 
                               record.rawData?.main_image || 
                               record.rawData?.main_image_url) ? 'transparent' : '#DB8633',
              border: 'none'
            }}
          >
            {!(record.rawData?.logo || 
               record.rawData?.logo_url || 
               record.rawData?.logoUrl ||
               record.imageUrl || 
               record.rawData?.imageUrl || 
               record.rawData?.main_image || 
               record.rawData?.main_image_url) && record.beneficiaryName.charAt(0)}
          </Avatar>
          <Text 
            strong 
            className="clickable-beneficiary-name"
            onClick={() => handleBeneficiaryClick(record.key, record)}
            style={{ cursor: 'pointer', color: '#DB8633' }}
          >
            {text}
          </Text>
        </Space>
      ),
      fixed: 'left' as const,
      width: 200,
    },
    { 
      title: 'Contact name', 
      dataIndex: 'contactName', 
      key: 'contactName', 
      render: (text: string) => {
        const displayText = (!text || text === 'N/A' || text === 'n/a') ? 'Not provided' : text;
        return <Text type="secondary">{displayText}</Text>;
      }, 
      width: 150 
    },
    { 
      title: 'Emails', 
      dataIndex: 'email', 
      key: 'email', 
      render: (text: string) => {
        const displayText = (!text || text === 'N/A' || text === 'n/a') ? 'Not provided' : text;
        return <Text type="secondary">{displayText}</Text>;
      }, 
      width: 200 
    },
    { 
      title: 'Contact number', 
      dataIndex: 'contactNumber', 
      key: 'contactNumber', 
      render: (text: string) => {
        const displayText = (!text || text === 'N/A' || text === 'n/a') ? 'Not provided' : text;
        return <Text type="secondary">{displayText}</Text>;
      }, 
      width: 150 
    },
    { 
      title: 'Bank Account', 
      dataIndex: 'bankAccount', 
      key: 'bankAccount', 
      render: (text: string) => {
        const displayText = (!text || text === 'N/A' || text === 'n/a') ? 'Not provided' : text;
        return <Text type="secondary">{displayText}</Text>;
      }, 
      width: 150 
    },
    {
      title: 'Donation',
      dataIndex: 'donation',
      key: 'donation',
      render: (text: string) => <Text strong style={{ color: '#DB8633' }}>{text}</Text>,
      width: 120,
    },
    {
      title: 'Date of join',
      dataIndex: 'dateOfJoin',
      key: 'dateOfJoin',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 130,
    },
    {
      title: 'City, State',
      dataIndex: 'cityState',
      key: 'cityState',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 130,
    },
    {
      title: 'Charity cause',
      dataIndex: 'beneficiaryCause',
      key: 'beneficiaryCause',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 200,
    },
    {
      title: 'Charity Type',
      dataIndex: 'beneficiaryType',
      key: 'beneficiaryType',
      render: (text: string) => {
        if (!text || text === 'N/A' || text === 'n/a' || text === 'Not provided') {
          return <Tag>Not provided</Tag>;
        }
        let color = 'default';
        if (text === 'Large' || text === 'International') color = 'blue';
        else if (text === 'Medium' || text === 'National') color = 'green';
        else if (text === 'Small' || text === 'Local') color = 'orange';
        return <Tag color={color}>{text}</Tag>;
      },
      width: 150,
    },
    {
      title: 'Donors',
      dataIndex: 'donors',
      key: 'donors',
      render: (text: number) => <Text strong>{text}</Text>,
      width: 100,
    },
    {
      title: 'Active/De-active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: any) => (
        <div className="toggle-switch">
          <div 
            className={`toggle ${active ? 'active' : 'inactive'}`}
            onClick={() => handleToggleChange(record.key, 'active')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 160,
    },
    {
      title: 'Enable/Disable',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: any) => (
        <div className="enable-disable-toggle">
          <div 
            className={`toggle ${enabled ? 'active' : 'inactive'}`}
            onClick={() => handleToggleChange(record.key, 'enabled')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 140,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 180,
      align: 'center' as const,
      render: (text: string, record: any) => {
        if (!record) {
          return null;
        }
        
        return (
          <div 
            className="actions-column-container"
            style={{ 
              display: 'flex', 
              gap: '6px', 
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              maxWidth: '100%',
              padding: '4px 0',
              boxSizing: 'border-box',
              margin: '0 auto'
            }}
          >
            <Button 
              size="middle"
              icon={<EditOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleBeneficiaryClick(record.key, record);
              }}
              className="edit-beneficiary-button"
              title="Edit Charity"
              style={{
                backgroundColor: '#fff7e6',
                borderColor: '#DB8633',
                color: '#DB8633',
                fontWeight: 600,
                width: '32px',
                height: '32px',
                padding: 0,
                minWidth: '32px',
                maxWidth: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                flexShrink: 0
              }}
            />
            <Button 
              size="middle"
              icon={<DeleteOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteBeneficiary(record);
              }}
              className="delete-beneficiary-button"
              title="Delete Charity"
              style={{
                backgroundColor: '#fff1f0',
                borderColor: '#ff4d4f',
                color: '#ff4d4f',
                width: '32px',
                height: '32px',
                padding: 0,
                minWidth: '32px',
                maxWidth: '32px',
                fontSize: '14px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                flexShrink: 0
              }}
            />
          </div>
        );
      },
    },
  ];


  return (
    <Layout className="standard-layout">
      <AdminSidebar
        activeKey="beneficiaries"
        mobileVisible={mobileSidebarVisible}
        onMobileToggle={() => setMobileSidebarVisible(!mobileSidebarVisible)}
      />

      <Layout className="standard-main-content">
        <Header className="beneficiaries-header">
          <div className="header-left">
            <Title level={2} style={{ margin: 0 }}>Charities</Title>
            <Text type="secondary" className="beneficiaries-count">
              {filteredBeneficiaries.length} Beneficiar{filteredBeneficiaries.length !== 1 ? 'ies' : 'y'} Found
              {filteredBeneficiaries.length !== totalBeneficiaries && ` (of ${totalBeneficiaries} total)`}
            </Text>
          </div>
          <div className="header-right">
            <Button 
              type="primary" 
              icon={<UserAddOutlined />}
              size="large"
              className="invite-beneficiary-btn"
              onClick={handleInviteBeneficiary}
            >
              Invite A Charity
            </Button>
          </div>
        </Header>

        <Content className="beneficiaries-content">
          <div className="content-wrapper">
            <DashboardSection
              title="Charity Highlights"
              subtitle="Network health at a glance"
              icon={<TrophyOutlined />}
            >
              <BeneficiaryHighlights data={highlights} />
            </DashboardSection>

            <DashboardSection
              title="All Charities"
              subtitle="Search, filter, and manage individual charity records"
              icon={<StarOutlined />}
            >
            {/* Search and Filter Bar */}
            <div className="search-filter-bar">
              <div className="search-section">
                <Search
                  placeholder="Search Charities"
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  className="beneficiary-search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              
              <div className="filter-section">
                <Text strong className="filter-label">Filters</Text>
                <div className="filter-dropdowns">
                  <Select
                    placeholder="Select Cause"
                    className="filter-dropdown"
                    size="large"
                    value={selectedCause}
                    onChange={(value) => setSelectedCause(value)}
                    allowClear
                  >
                    {uniqueCauses.map((cause) => (
                      <Option key={cause} value={cause}>
                        {cause}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    placeholder="Select Duration"
                    className="filter-dropdown"
                    size="large"
                    value={selectedDuration}
                    onChange={(value) => setSelectedDuration(value)}
                    allowClear
                  >
                    <Option value="short">Short Term</Option>
                    <Option value="long">Long Term</Option>
                    <Option value="ongoing">Ongoing</Option>
                  </Select>
                  
                  <Select
                    placeholder="Charity Type"
                    className="filter-dropdown"
                    size="large"
                    value={selectedType}
                    onChange={(value) => setSelectedType(value)}
                    allowClear
                  >
                    {uniqueTypes.map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    placeholder="City, State"
                    className="filter-dropdown"
                    size="large"
                    value={selectedLocation}
                    onChange={(value) => setSelectedLocation(value)}
                    allowClear
                  >
                    {uniqueLocations.map((location) => (
                      <Option key={location} value={location}>
                        {location}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Active / Inactive"
                    className="filter-dropdown"
                    size="large"
                    value={selectedActiveStatus}
                    onChange={(value) => setSelectedActiveStatus(value)}
                    allowClear
                  >
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>

                  <Button
                    size="large"
                    disabled={!searchTerm && !selectedCause && !selectedDuration && !selectedType && !selectedLocation && !selectedActiveStatus}
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCause(undefined);
                      setSelectedDuration(undefined);
                      setSelectedType(undefined);
                      setSelectedLocation(undefined);
                      setSelectedActiveStatus(undefined);
                    }}
                    style={{ color: '#DB8633', borderColor: '#DB8633' }}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            </div>

            {/* Beneficiaries Table */}
            <div className="beneficiaries-table-section">
              <Spin spinning={loading}>
                <Table
                  dataSource={paginatedBeneficiaries}
                  columns={columns}
                  pagination={false}
                  size="middle"
                  className="beneficiaries-table"
                  rowClassName="beneficiary-row"
                  scroll={{ x: 1800 }}
                  bordered={false}
                  onChange={(_, __, sorter) => {
                    if (Array.isArray(sorter)) {
                      return;
                    }
                    const nextField = (sorter as any).field as string | undefined;
                    const nextOrder = (sorter as any).order as 'ascend' | 'descend' | null | undefined;
                    setSortField(nextField || null);
                    setSortOrder(nextOrder || null);
                  }}
                  locale={{
                    emptyText: error ? `Error: ${error}` : 'No charities found'
                  }}
                />
              </Spin>
              
              {/* Pagination */}
              <div className="pagination-section">
                <Pagination
                  current={currentPage}
                  total={filteredBeneficiaries.length}
                  pageSize={pageSize}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  onChange={handlePageChange}
                  className="beneficiaries-pagination"
                />
              </div>
            </div>
            </DashboardSection>
          </div>
        </Content>
      </Layout>

      {/* Invite Beneficiary Modal */}
      <InviteBeneficiaryModal
        visible={inviteModalVisible}
        onCancel={handleInviteModalCancel}
        onSubmit={handleInviteModalSubmit}
      />

      {/* Beneficiary Profile Modal */}
      {profileVisible && selectedBeneficiaryId && selectedBeneficiaryData && (
        <BeneficiaryProfile
          beneficiaryId={selectedBeneficiaryId}
          beneficiaryData={selectedBeneficiaryData}
          onClose={handleProfileClose}
          onUpdate={handleBeneficiaryUpdate}
        />
      )}

      {/* Delete Beneficiary Confirmation Modal */}
      <Modal
        title="Delete Charity"
        open={isDeleteBeneficiaryModalVisible}
        onOk={confirmDeleteBeneficiary}
        onCancel={() => {
          setIsDeleteBeneficiaryModalVisible(false);
          setDeletingBeneficiary(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        {deletingBeneficiary && (
          <div>
            <p>Are you sure you want to permanently delete this charity?</p>
            <div style={{ 
              padding: '16px', 
              background: '#fff7e6', 
              borderRadius: '4px',
              marginTop: '16px',
              marginBottom: '16px'
            }}>
              <Text strong>Charity Details:</Text>
              <br />
              <Text>Name: {deletingBeneficiary.beneficiaryName || deletingBeneficiary.name}</Text>
              {deletingBeneficiary.cityState && (
                <>
                  <br />
                  <Text>Location: {deletingBeneficiary.cityState}</Text>
                </>
              )}
              {deletingBeneficiary.beneficiaryCause && (
                <>
                  <br />
                  <Text>Category: {deletingBeneficiary.beneficiaryCause}</Text>
                </>
              )}
              {deletingBeneficiary.donation && (
                <>
                  <br />
                  <Text>Total Donations: {deletingBeneficiary.donation}</Text>
                </>
              )}
            </div>
            <p style={{ color: '#ff4d4f', marginBottom: 0 }}>
              <Text strong>Warning:</Text> This action cannot be undone. All charity data including donations, images, and other associated information will be permanently deleted.
            </p>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default Beneficiaries; 